<template>
  <div class="orders-layout">
    <!-- 主内容区 -->
    <main class="orders-content">
      <!-- 搜索和筛选区域 -->
      <div class="filter-section">
        <div class="filter-header">
          <h1 class="page-title">订单管理</h1>
        </div>
        <div class="filter-controls">
          <div class="search-bar">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索订单编号、商品名称或收银员..."
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <button class="search-button" @click="handleSearch">搜索</button>
            <button
              class="refresh-orders-btn"
              @click="refreshOrders"
              :disabled="loading"
              title="刷新订单列表"
            >
              <img
                src="/img/refresh.png"
                alt="刷新"
                class="refresh-icon"
                :class="{ 'spinning': loading }"
              >
            </button>
          </div>
          <div class="status-group">
            <div class="filter-label">状态：</div>
            <div class="status-filters">
              <div
                v-for="status in orderStatuses"
                :key="status.id"
                :class="['status-filter', { active: selectedStatus === status.id }]"
                @click="selectStatus(status.id)"
              >
                {{ status.name }}
              </div>
            </div>
          </div>
          <button class="reset-button" @click="resetFilters">重置</button>
        </div>
      </div>

      <!-- 订单统计 -->
      <div class="order-stats">
        <div class="stat-card">
          <div class="stat-label">今日订单</div>
          <div class="stat-value">{{ todayOrderCount }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">今日销售额</div>
          <div class="stat-value">¥{{ todaySalesAmount.toFixed(2) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">今日退款</div>
          <div class="stat-value refund">¥{{ todayRefundAmount.toFixed(2) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">今日出货</div>
          <div class="stat-value">{{ todayShipmentQty }}</div>
        </div>
      </div>

      <!-- 订单表格 -->
      <div class="orders-table-container">
        <table class="orders-table">
          <thead>
            <tr>
              <th>订单信息</th>
              <th>购买商品</th>
              <th>数量</th>
              <th class="sortable-header" @click="toggleAmountSort">
                订单金额
                <span class="sort-indicator">
                  <span v-if="amountSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="amountSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th>收银员</th>
              <th>会员</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="loading-data">
                <div class="loading-spinner"></div>
                加载中...
              </td>
            </tr>
            <tr v-else-if="orders.length === 0">
              <td colspan="7" class="no-data">暂无订单数据</td>
            </tr>
            <tr v-for="order in paginatedOrders" :key="order.id" class="order-row" @click="showOrderDetail(order)">
              <td class="order-info">
                <div class="order-number">{{ order.orderNo }}</div>
                <div class="order-time">{{ formatTime(order.createTime) }}</div>
                <span :class="['status-tag', getStatusClass(order.status)]">
                  {{ getStatusName(order.status) }}
                </span>
              </td>
              <td class="order-items">
                <div class="items-list">
                  <div v-for="item in order.items.slice(0, 2)" :key="item.id" class="item-row">
                    <div class="item-info-column">
                      <div class="item-name">
                        {{ item.name }}<span v-if="item.isGift" class="gift-tag-small">赠</span>
                      </div>
                      <div class="item-details">
                        <span class="item-quantity">×{{ item.quantity }}</span>
                        <span class="item-price">¥{{ item.price.toFixed(2) }}</span>
                        <span class="item-subtotal">小计:¥{{ item.subtotal.toFixed(2) }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="order.items.length > 2" class="more-items">
                    还有{{ order.items.length - 2 }}件商品...
                  </div>
                </div>
              </td>
              <td class="total-quantity">{{ getTotalQuantity(order.items) }}</td>
              <td class="order-amount">
                <div class="amount-details">
                  <div class="amount-row">
                    <span class="amount-label">商品总价:</span>
                    <span class="amount-value">¥{{ order.originalAmount ? order.originalAmount.toFixed(2) : '0.00' }}</span>
                  </div>
                  <div class="amount-row">
                    <span class="amount-label">实收金额:</span>
                    <span class="amount-value primary">¥{{ order.totalAmount.toFixed(2) }}</span>
                  </div>
                  <div v-if="order.discountAmount" class="amount-row">
                    <span class="amount-label">抹零金额:</span>
                    <span class="amount-value discount">¥{{ order.discountAmount.toFixed(2) }}</span>
                  </div>
                  <div v-if="order.refundAmount" class="amount-row">
                    <span class="amount-label">退款金额:</span>
                    <span class="amount-value refund">¥{{ order.refundAmount.toFixed(2) }}</span>
                  </div>
                </div>
              </td>
              <td class="cashier">{{ order.cashier }}</td>
              <td class="member">
                <div v-if="order.member" class="member-info">
                  <div class="member-name">{{ order.member.name }}</div>
                  <div class="member-phone">{{ order.member.phone }}</div>
                </div>
                <span v-else>-</span>
              </td>
              <td class="order-actions">
                <button
                  v-if="order.status === 1 || order.status === 3"
                  class="refund-btn"
                  @click.stop="handleRefund(order)"
                  :disabled="loading"
                >
                  退款
                </button>
                <span v-else class="no-action">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <span class="pagination-info">
          显示 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, total) }}
          共 {{ total }} 条
        </span>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="currentPage === 1 || loading"
            @click="changePage(currentPage - 1)"
          >
            上一页
          </button>
          <span class="page-number">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="page-btn"
            :disabled="currentPage === totalPages || loading"
            @click="changePage(currentPage + 1)"
          >
            下一页
          </button>
        </div>
      </div>
    </main>

    <!-- 订单详情弹窗 -->
    <div v-if="selectedOrder" class="order-detail-modal" @click="closeOrderDetail">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>订单详情 - {{ selectedOrder.orderNo }}</h3>
          <button class="close-btn" @click="closeOrderDetail">×</button>
        </div>
        <div class="modal-body">
          <div class="order-info">
            <div class="info-row">
              <span class="label">下单时间：</span>
              <span class="value">{{ formatTime(selectedOrder.createTime) }}</span>
            </div>
            <div class="info-row">
              <span class="label">收银员：</span>
              <span class="value">{{ selectedOrder.cashier }}</span>
            </div>
            <div class="info-row">
              <span class="label">会员信息：</span>
              <span class="value">
                <div v-if="selectedOrder.member" class="member-detail">
                  <div class="member-name">{{ selectedOrder.member.name }}</div>
                  <div class="member-phone">{{ selectedOrder.member.phone }}</div>
                </div>
                <span v-else>-</span>
              </span>
            </div>
            <div class="info-row">
              <span class="label">订单状态：</span>
              <span class="value">{{ getStatusName(selectedOrder.status) }}</span>
            </div>
            <div v-if="selectedOrder.remark" class="info-row">
              <span class="label">备注：</span>
              <span class="value">{{ selectedOrder.remark }}</span>
            </div>
          </div>
          <div class="order-items">
            <h4>商品明细</h4>
            <table class="items-table">
              <thead>
                <tr>
                  <th>商品名称</th>
                  <th>单价</th>
                  <th>购买数量</th>
                  <th>已退数量</th>
                  <th>剩余数量</th>
                  <th>小计</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedOrder.items" :key="item.id">
                  <td class="item-name">
                    <div class="item-text">
                      {{ item.name }}
                      <span v-if="item.isGift" class="gift-tag">赠品</span>
                      <span v-if="item.itemStatus === 2" class="refunded-tag">已退款</span>
                    </div>
                  </td>
                  <td class="item-price">¥{{ item.price.toFixed(2) }}</td>
                  <td class="item-quantity">{{ item.quantity }}</td>
                  <td class="item-refunded">
                    <span v-if="item.refundedQuantity > 0" class="refunded-quantity">{{ item.refundedQuantity }}</span>
                    <span v-else class="no-refund">0</span>
                  </td>
                  <td class="item-remaining">
                    <span v-if="item.remainingQuantity > 0" class="remaining-quantity">{{ item.remainingQuantity }}</span>
                    <span v-else class="no-remaining">0</span>
                  </td>
                  <td class="item-total">¥{{ item.subtotal.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="order-summary">
              <div class="summary-row">
                <span>商品总价：</span>
                <span>¥{{ selectedOrder.originalAmount ? selectedOrder.originalAmount.toFixed(2) : '0.00' }}</span>
              </div>
              <div class="summary-row primary">
                <span>实收金额：</span>
                <span>¥{{ selectedOrder.totalAmount.toFixed(2) }}</span>
              </div>
              <div v-if="selectedOrder.discountAmount && selectedOrder.discountAmount > 0" class="summary-row discount">
                <span>抹零金额：</span>
                <span>¥{{ selectedOrder.discountAmount.toFixed(2) }}</span>
              </div>
              <div v-if="selectedOrder.refundAmount && selectedOrder.refundAmount > 0" class="summary-row refund">
                <span>退款金额：</span>
                <span>¥{{ selectedOrder.refundAmount.toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 退款确认对话框 -->
    <RefundModal
      :visible="refundModalVisible"
      :order-info="refundOrderInfo"
      @confirm="handleRefundConfirm"
      @cancel="handleRefundCancel"
    />
  </div>
</template>

<script>
import { orderApi } from '../api/index.js'
import RefundModal from './RefundModal.vue'
import toast from '../utils/toast.js'

export default {
  name: 'OrdersView',
  components: {
    RefundModal
  },
  props: {
    userInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      searchKeyword: '',
      selectedStatus: 0,
      currentPage: 1,
      amountSortOrder: null, // null, 'asc', 'desc'
      pageSize: 15,
      selectedOrder: null,
      orderStatuses: [
        { id: 0, name: '全部' },
        { id: 1, name: '已支付' },
        { id: 2, name: '已退款' },
        { id: 3, name: '部分退款' }
      ],
      paymentMethods: [
        { id: 1, name: '现金', class: 'cash' },
        { id: 2, name: '微信', class: 'wechat' },
        { id: 3, name: '支付宝', class: 'alipay' },
        { id: 4, name: '银行卡', class: 'card' }
      ],
      orders: [],
      total: 0,
      loading: false,
      todayStats: {
        todayOrderCount: 0,
        todaySalesAmount: 0.0,
        todayRefundAmount: 0.0,
        todayShipmentQty: 0
      },
      // 退款模态框相关
      refundModalVisible: false,
      refundOrderInfo: null
    }
  },
  computed: {
    paginatedOrders() {
      return this.orders
    },
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    todayOrderCount() {
      return this.todayStats.todayOrderCount
    },
    todaySalesAmount() {
      return this.todayStats.todaySalesAmount
    },
    todayRefundAmount() {
      return this.todayStats.todayRefundAmount
    },
    todayShipmentQty() {
      return this.todayStats.todayShipmentQty || 0
    }
  },
  methods: {
    async loadOrders() {
      try {
        this.loading = true

        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }

        if (this.searchKeyword) {
          params.order_no = this.searchKeyword
        }

        if (this.selectedStatus !== 0) {
          params.order_status = this.selectedStatus
        }

        const response = await orderApi.getCashierOrderList(params)

        if (response.code === 0) {
          // 处理list为null或空的情况
          if (!response.data.list || response.data.list.length === 0) {
            this.orders = []
            this.total = response.data.total || 0

            if (response.data.stats) {
              this.todayStats = {
                todayOrderCount: response.data.stats.today_order_count || 0,
                todaySalesAmount: response.data.stats.today_sales_amount || 0.0,
                todayRefundAmount: response.data.stats.today_refund_amount || 0.0,
                todayShipmentQty: response.data.stats.today_shipment_qty || 0
              }
            }
            return
          }

          this.orders = response.data.list.map(order => ({
            id: order.id,
            orderNo: order.order_no,
            createTime: new Date(order.order_time),
            totalAmount: order.final_amount,
            originalAmount: order.original_amount,
            discountAmount: order.discount_amount,
            refundAmount: order.refund_amount,
            itemCount: order.order_items ? order.order_items.length : 0,
            cashier: order.cashier ? order.cashier.nickname || order.cashier.username : '未知',
            member: order.member,
            paymentMethod: order.payment_method || 1,
            status: order.order_status,
            paymentStatus: order.payment_status,
            remark: order.remark,
            memberId: order.member_id,
            items: order.order_items ? order.order_items.map(item => ({
              id: item.ID, // order_item_id (API返回的是大写的ID)
              name: item.productName,
              price: item.unitPrice,
              quantity: item.quantity,
              subtotal: item.subtotal,
              isGift: item.isGift,
              itemStatus: item.itemStatus, // 添加商品状态
              refundedQuantity: item.refundedQuantity || 0, // 已退款数量
              remainingQuantity: item.remainingQuantity || item.quantity, // 剩余可退款数量
              productId: item.productId, // 保留productId供参考
              product: item.product
            })) : []
          }))

          this.total = response.data.total

          if (response.data.stats) {
            this.todayStats = {
              todayOrderCount: response.data.stats.today_order_count || 0,
              todaySalesAmount: response.data.stats.today_sales_amount || 0.0,
              todayRefundAmount: response.data.stats.today_refund_amount || 0.0,
              todayShipmentQty: response.data.stats.today_shipment_qty || 0
            }
          }
        } else {
          alert('获取订单列表失败：' + response.msg)
        }
      } catch (error) {
        console.error('加载订单数据失败:', error)
        alert('加载订单数据失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    toggleAmountSort() {
      if (this.amountSortOrder === null) {
        this.amountSortOrder = 'asc'
      } else if (this.amountSortOrder === 'asc') {
        this.amountSortOrder = 'desc'
      } else {
        this.amountSortOrder = null
      }
      this.currentPage = 1
      this.loadOrders()
    },
    selectStatus(statusId) {
      this.selectedStatus = statusId
      this.currentPage = 1
      this.loadOrders()
    },
    handleSearch() {
      if (!this.searchKeyword.trim()) {
        alert('请输入搜索关键词')
        return
      }
      this.currentPage = 1
      this.loadOrders()
    },
    resetFilters() {
      this.searchKeyword = ''
      this.selectedStatus = 0
      this.amountSortOrder = null
      this.currentPage = 1
      this.loadOrders()
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadOrders()
      }
    },
    getStatusName(statusId) {
      const status = this.orderStatuses.find(s => s.id === statusId)
      return status ? status.name : '未知'
    },
    getStatusClass(statusId) {
      const classes = {
        1: 'completed',  // 已支付
        2: 'refunded',   // 已退款
        3: 'partial-refund'  // 部分退款
      }
      return classes[statusId] || 'unknown'
    },
    getPaymentMethodName(methodId) {
      const method = this.paymentMethods.find(m => m.id === methodId)
      return method ? method.name : '未知'
    },
    getPaymentClass(methodId) {
      const method = this.paymentMethods.find(m => m.id === methodId)
      return method ? method.class : 'unknown'
    },
    formatTime(time) {
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    showOrderDetail(order) {
      this.selectedOrder = order
    },
    closeOrderDetail() {
      this.selectedOrder = null
    },
    async handleRefund(order) {
      try {
        this.loading = true

        // 先获取订单详情（退款前调用）
        const response = await orderApi.getCashierOrderList({
          order_no: order.orderNo,
          pageSize: 1
        })

        if (response.code === 0 && response.data.list.length > 0) {
          const orderDetail = response.data.list[0]
          this.refundOrderInfo = {
            orderNo: orderDetail.order_no,
            amount: orderDetail.final_amount || orderDetail.original_amount,
            orderId: orderDetail.id,
            cashier: orderDetail.cashier ? orderDetail.cashier.nickname || orderDetail.cashier.username : '未知',
            items: orderDetail.order_items ? orderDetail.order_items.map(item => ({
              id: item.ID, // order_item_id (注意API返回的是大写的ID)
              name: item.productName,
              price: item.unitPrice,
              quantity: item.quantity,
              subtotal: item.subtotal,
              isGift: item.isGift || false,
              itemStatus: item.itemStatus,
              refundedQuantity: item.refundedQuantity || 0, // 已退款数量
              remainingQuantity: item.remainingQuantity || item.quantity, // 剩余可退款数量
              productId: item.productId // 保留productId供参考
            })) : []
          }
          this.refundModalVisible = true
        } else {
          toast.error('获取订单详情失败：' + (response.msg || '未知错误'))
        }
      } catch (error) {
        console.error('获取订单详情失败:', error)
        toast.error('获取订单详情失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    handleRefundCancel() {
      this.refundModalVisible = false
      this.refundOrderInfo = null
    },
    async handleRefundConfirm(refundData) {
      if (!this.refundOrderInfo) return

      try {
        this.loading = true

        const response = await orderApi.refundOrder(refundData)

        if (response.code === 0) {
          toast.success('退款成功！')
          this.refundModalVisible = false
          this.refundOrderInfo = null
          // 重新加载订单数据
          this.loadOrders()
        } else {
          toast.error('退款失败：' + response.msg)
        }
      } catch (error) {
        console.error('退款失败:', error)
        toast.error('退款失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    getTotalQuantity(items) {
      return items.reduce((total, item) => total + item.quantity, 0)
    },

    // 刷新订单列表
    async refreshOrders() {
      console.log('刷新订单列表...')
      try {
        // 重置到第一页并重新加载
        this.currentPage = 1
        await this.loadOrders()
        toast.success('订单列表已刷新')
      } catch (error) {
        console.error('刷新订单列表失败:', error)
        toast.error('刷新订单列表失败')
      }
    }
  },
  mounted() {
    this.loadOrders()
  }
}
</script>

<style scoped>
.orders-layout {
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 主内容区 */
.orders-content {
  flex: 1;
  height: 100vh;
  padding: 24px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-sizing: border-box;
  min-width: 0;
}

.page-title {
  margin: 0 0 20px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.5px;
  position: relative;
  padding-left: 20px;
}

.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 28px;
  background: linear-gradient(180deg, #667eea, #764ba2);
  border-radius: 2px;
}

/* 筛选区域样式 */
.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 450px;
}

.search-input {
  border: 2px solid #e1e5e9;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  font-size: 14px;
  width: 100%;
  outline: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #f8fafc;
  color: #334155;
  font-weight: 500;
}

.search-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: white;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 80px;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.search-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.refresh-orders-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #52c41a, #389e0d);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(82, 196, 26, 0.3);
  padding: 0;
  flex-shrink: 0;
}

.refresh-orders-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
}

.refresh-orders-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.refresh-orders-btn .refresh-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;
  transition: all 0.3s ease;
}

.refresh-orders-btn:hover:not(:disabled) .refresh-icon {
  opacity: 1;
}

.refresh-orders-btn .refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

.reset-button {
  background: #f1f5f9;
  color: #64748b;
  border: 2px solid #e2e8f0;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 80px;
  white-space: nowrap;
}

.reset-button:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 24px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.filter-header {
  margin-bottom: 20px;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.status-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label {
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  font-size: 14px;
  letter-spacing: 0.2px;
}

.status-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.status-filter {
  padding: 0 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  font-size: 13px;
  background: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  min-width: 70px;
  text-align: center;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #6b7280;
}

.status-filter:hover {
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.status-filter.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

/* 统计卡片 */
.order-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.stat-value {
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-value.warning {
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-value.refund {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 表格 */
.orders-table-container {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);
  margin-bottom: 24px;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.orders-table th {
  background: #f8fafc;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  font-size: 13px;
  white-space: nowrap;
  position: relative;
}

.orders-table th:nth-child(1) { width: 18%; } /* 订单信息 */
.orders-table th:nth-child(2) { width: 32%; } /* 购买商品 */
.orders-table th:nth-child(3) { width: 6%; }  /* 数量 */
.orders-table th:nth-child(4) { width: 20%; } /* 订单金额 */
.orders-table th:nth-child(5) { width: 8%; }  /* 收银员 */
.orders-table th:nth-child(6) { width: 8%; }  /* 会员 */
.orders-table th:nth-child(7) { width: 8%; }  /* 操作 */

.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: all 0.3s ease;
}

.sortable-header:hover {
  background: #e2e8f0;
}

.sort-indicator {
  margin-left: 8px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.sort-asc { color: #10b981; }
.sort-desc { color: #ef4444; }
.sort-none { color: #94a3b8; }

.orders-table td {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 14px;
  color: #374151;
}

.order-row {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.order-row:hover {
  background: rgba(102, 126, 234, 0.05);
  transform: scale(1.005);
}

.order-row:hover::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #667eea, #764ba2);
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
}

.order-number {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  color: #667eea;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.3px;
}

.order-time {
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
}

.order-items {
  padding: 8px 5px;
}

.items-list {
  min-height: 20px;
}

.item-row {
  margin-bottom: 8px;
}

.item-row:last-child {
  margin-bottom: 0;
}

.item-info-small {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.item-image-small {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.item-info-column {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  width: 100%;
}

.item-name {
  color: #333;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-details {
  display: flex;
  font-size: 11px;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
}

.item-quantity {
  color: #1890ff;
  font-weight: 500;
  flex-shrink: 0;
}

.item-price {
   color: #666;
  font-weight: 500;
  flex-shrink: 0;
}
 

.gift-tag-small {
  display: inline-block;
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 10px;
  margin-left: 8px;
  flex-shrink: 0;
  line-height: 1;
  vertical-align: super;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2);
}

.more-items {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  margin-top: 8px;
  background: rgba(102, 126, 234, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
  display: inline-block;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

 
 

.item-subtotal {
  color: #1890ff;
  font-size: 12px;
  font-weight: 500;
}

.total-quantity {
  text-align: center;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.order-amount {
  padding: 8px 5px;
}

.amount-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
}

.amount-label {
  color: #666;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.amount-value {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  text-align: right;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.amount-value.primary {
  color: #1890ff;
  font-weight: 700;
}

.amount-value.discount {
  color: #fa8c16;
}

.amount-value.refund {
  color: #f5222d;
}

.cashier {
  color: #333;
}

.member {
  color: #333;
}

.member-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.member-name {
  font-weight: 500;
  color: #333;
}

.member-phone {
  font-size: 12px;
  color: #666;
}

.payment-tag {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
}

.payment-tag.cash { background-color: #e6f7ff; color: #1890ff; }
.payment-tag.wechat { background-color: #f6ffed; color: #52c41a; }
.payment-tag.alipay { background-color: #fff2e8; color: #fa8c16; }
.payment-tag.card { background-color: #f9f0ff; color: #722ed1; }

.status-tag {
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  border: none;
  text-transform: uppercase;
  display: inline-block;
  align-self: flex-start;
  margin-top: 4px;
}

.status-tag.completed {
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: white;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.2);
}

.status-tag.pending {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.2);
}

.status-tag.cancelled {
  background: linear-gradient(135deg, #f87171, #ef4444);
  color: white;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.2);
}

.status-tag.refunded {
  background: linear-gradient(135deg, #94a3b8, #64748b);
  color: white;
  box-shadow: 0 4px 15px rgba(100, 116, 139, 0.2);
}

.status-tag.partial-refund {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.2);
}

/* 操作按钮 */
.order-actions {
  text-align: center;
}

.refund-btn {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.refund-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
}

.refund-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.no-action {
  color: #999;
  font-size: 14px;
}

.no-data {
  text-align: center;
  color: #64748b;
  padding: 60px;
  font-size: 16px;
  font-weight: 600;
  background: rgba(248, 250, 252, 0.6);
  border-radius: 16px;
  margin: 20px;
  border: 2px dashed rgba(102, 126, 234, 0.2);
}

.loading-data {
  text-align: center;
  color: #64748b;
  padding: 60px;
  font-size: 16px;
  font-weight: 600;
}

.loading-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid rgba(102, 126, 234, 0.1);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  margin-right: 12px;
  vertical-align: middle;
  position: relative;
}

.loading-spinner::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #764ba2;
  animation: spin 1.5s cubic-bezier(0.4, 0, 0.2, 1) reverse infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 分页 */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 24px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-sizing: border-box;
}

.pagination-info {
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-btn {
  background: white;
  border: 2px solid #e5e7eb;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.page-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.page-number {
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  padding: 8px 16px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  min-width: 60px;
  text-align: center;
}

/* 订单详情弹窗 */
.order-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 24px 24px 0 0;
}

.modal-header h3 {
  margin: 0;
  color: white;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.3px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.modal-body {
  padding: 32px;
}

.order-info {
  margin-bottom: 32px;
  background: rgba(248, 250, 252, 0.6);
  padding: 24px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.info-row {
  display: flex;
  margin-bottom: 16px;
  align-items: center;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  width: 120px;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.info-row .value {
  flex: 1;
  color: #1e293b;
  font-weight: 600;
  font-size: 15px;
  background: white;
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.member-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.member-detail .member-name {
  font-weight: 600;
  color: #333;
}

.member-detail .member-phone {
  font-size: 14px;
  color: #666;
}

.order-items h4 {
  margin: 0 0 20px 0;
  color: #1e293b;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.2px;
  position: relative;
  padding-left: 20px;
}

.order-items h4::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: linear-gradient(180deg, #667eea, #764ba2);
  border-radius: 2px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  table-layout: fixed;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
}

.items-table th:nth-child(1) { width: 35%; } /* 商品名称 */
.items-table th:nth-child(2) { width: 12%; } /* 单价 */
.items-table th:nth-child(3) { width: 12%; } /* 购买数量 */
.items-table th:nth-child(4) { width: 12%; } /* 已退数量 */
.items-table th:nth-child(5) { width: 12%; } /* 剩余数量 */
.items-table th:nth-child(6) { width: 17%; } /* 小计 */

.items-table th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 16px 12px;
  text-align: left;
  font-weight: 700;
  color: white;
  border: none;
  font-size: 13px;
  white-space: nowrap;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.items-table td {
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 13px;
  background: white;
}

.items-table td:not(.item-name) {
  white-space: nowrap;
}

.item-total {
  text-align: right;
  font-weight: 600;
  color: #e74c3c;
}

.gift-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 4px 10px;
  font-size: 10px;
  margin-left: 8px;
  white-space: nowrap;
  line-height: 1;
  flex: 0 0 auto;
  min-width: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2);
}

.refunded-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f87171, #ef4444);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 4px 10px;
  font-size: 10px;
  margin-left: 8px;
  white-space: nowrap;
  line-height: 1;
  flex: 0 0 auto;
  min-width: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

.item-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.item-image {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.item-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* 订单详情表格中的商品名称样式 */
.items-table .item-text {
  line-height: 1.4;
  word-wrap: break-word;
  word-break: break-all;
  flex-wrap: wrap;
  align-items: center;
}

.items-table .item-name {
  color: #333;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.4;
  flex: 0 0 auto;
}

.item-refunded, .item-remaining {
  text-align: center;
  font-weight: 500;
}

.refunded-quantity {
  color: #f5222d;
}

.remaining-quantity {
  color: #1890ff;
}

.no-refund, .no-remaining {
  color: #999;
  font-style: italic;
}

.order-summary {
  border-top: 2px solid rgba(102, 126, 234, 0.1);
  padding-top: 24px;
  margin-top: 24px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05));
  padding: 24px;
  border-radius: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  align-items: center;
  padding: 8px 0;
}

.summary-row:last-child {
  margin-bottom: 0;
}

.summary-row.refund {
  color: #ef4444;
  font-weight: 600;
}

.summary-row.primary {
  font-weight: 800;
  font-size: 18px;
  color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.2);
  margin-top: 8px;
}

.summary-row.discount {
  color: #f59e0b;
  font-weight: 600;
}
</style>